import { Color3, GroundMesh, Matrix, Mesh, Quaternion, Scene, ShaderMaterial, Vector3, VertexData } from '@babylonjs/core';

/**
 * Defines an impact position to simulate impact of objects moving on the grass.
 */
export interface GrassImpactObject {
  /**
   * Reference to the position of the object that is moving on the grass.
   */
  position: Vector3;
  /**
   * Radius of the impact.
   */
  radius: number;
  /**
   * Strength of the impact.
   */
  strength: number;
}

/**
 * Configures the grass builder
 */
export interface GrassBuilderConfig {
  /**
   * Blade height
   */
  bladeHeight: number;

  /**
   * Blade width at base
   */
  bladeWidth: number;

  /**
   * Wind animation speed
   */
  windSpeed: number;

  /**
   * Max displacement amount
   */
  windStrength: number;

  /**
   * Base color (bottom)
   */
  baseColor: Color3;

  /**
   * Tip color (top)
   */
  tipColor: Color3;

  /**
   * Blade positions
   */
  positions: Vector3[];

  /**
   * Minimum scale (randomized between minScale and maxScale)
   * Default: 1
   */
  minScale?: number;

  /**
   * Maximum scale (randomized between minScale and maxScale)
   * Default: 1
   */
  maxScale?: number;

  /**
   * Minimum rotation (randomized between minRotation and maxRotation)
   * Default: 0
   */
  minRotation?: number;

  /**
   * Maximum rotation (randomized between minRotation and maxRotation)
   * Default: Math.PI * 2
   */
  maxRotation?: number;

  /**
   * Whether the grass should receive shadows
   * Default: false
   */
  receiveShadows?: boolean;

  /**
   * Set the desired distance to render grass.
   * This is an optimization to not render grass that is too far away.
   * The grass will be culled based on the camera's view frustum.
   * 0 means disabled culling.
   * Default: 0
   */
  frustumDepth?: number;

  /**
   * Impact positions can be configured optionally to simulate impact of objects on the grass.
   */
  impactObjects?: GrassImpactObject[];
}

/**
 * Builds stylized reactive, animated grass based on thin instances and a shader material.
 * The grass can be configured with colors, dimensions, wind influence and rotation.
 * As a performance optimization frustum culling is built into the vertex shader.
 * It can be affected/impacted by objects moving on the grass.
 *
 * https://playground.babylonjs.com/#NGA19V
 *
 * @author daniel.peer@gmx.net
 */
export class GrassBuilder {
  public grassMesh: Mesh;
  private shaderMaterial: ShaderMaterial;
  private time: number = 0;

  private frustumStart: Vector3;
  private frustumDepth: number;
  private frustumDirection: Vector3;
  private impactObjects: GrassImpactObject[] = [];

  constructor(
    public scene: Scene,
    public rng: () => number = Math.random
  ) {}

  public create(config: GrassBuilderConfig): Promise<void> {
    if (!config) {
      return;
    }

    if (config.frustumDepth) {
      this.frustumStart = this.scene.activeCamera.position;
      this.frustumDepth = config.frustumDepth;
    } else {
      this.frustumStart = Vector3.Zero();
      this.frustumDepth = 0;
    }

    this.frustumDirection = Vector3.Forward();

    if (config.impactObjects) {
      this.impactObjects = config.impactObjects;
    }

    // Create the base mesh (single blade)
    this.createGrassBladeMesh(config);

    // Create the material
    this.createGrassMaterial(config);

    // Generate thin instances
    this.generateInstances(config);
  }

  public update(deltaTime: number): void {
    if (this.shaderMaterial) {
      // Wrap time to avoid precision issues
      // 3600 seconds = 1 hour. This should be enough to not notice the wrap.
      // The shader uses sin(time), so wrapping at a multiple of 2*PI is best to avoid pops.
      const dt = this.scene.getEngine().getDeltaTime() / 1000.0;
      this.time += dt;
      if (this.time > 3600) {
        this.time %= 3600;
      }

      if (this.frustumDepth > 0 && this.scene.activeCamera) {
        this.scene.activeCamera.getDirectionToRef(Vector3.Forward(), this.frustumDirection);
        //this.frustumStart.copyFrom(this.scene.activeCamera.globalPosition);
      }

      this.shaderMaterial.setFloat('iTime', this.time);
      this.shaderMaterial.setMatrix('viewProjection', this.scene.getTransformMatrix());
      this.shaderMaterial.setVector3('frustumStart', this.frustumStart);
      this.shaderMaterial.setFloat('frustumDepth', this.frustumDepth);
      this.shaderMaterial.setVector3('frustumDirection', this.frustumDirection);

      if (this.impactObjects && this.impactObjects.length > 0) {
        const count = Math.min(this.impactObjects.length, 10);
        this.shaderMaterial.setInt('impactCount', count);

        const posX: number[] = new Array(10).fill(0);
        const posZ: number[] = new Array(10).fill(0);
        const radius: number[] = new Array(10).fill(0);
        const strength: number[] = new Array(10).fill(0);

        for (let i = 0; i < count; i++) {
          posX[i] = this.impactObjects[i].position.x;
          posZ[i] = this.impactObjects[i].position.z;
          radius[i] = this.impactObjects[i].radius;
          strength[i] = this.impactObjects[i].strength;
        }

        this.shaderMaterial.setFloats('impactPosX', posX);
        this.shaderMaterial.setFloats('impactPosZ', posZ);
        this.shaderMaterial.setFloats('impactRadius', radius);
        this.shaderMaterial.setFloats('impactStrength', strength);
      } else {
        this.shaderMaterial.setInt('impactCount', 0);
      }
    }
  }

  private createGrassBladeMesh(config: GrassBuilderConfig): void {
    const bladeHeight = config.bladeHeight;
    const bladeWidth = config.bladeWidth;

    // Simple 4-vertex blade (2 triangles)
    //  2--3  (tip, width 0)
    //  | /|
    //  |/ |
    //  0--1  (base, width bladeWidth)

    // Actually simple triangle:
    //       2 (top, center)
    //      / \
    //     0---1 (bottom)

    const positions = [
      -bladeWidth / 2,
      0,
      0, // 0: Bottom Left
      bladeWidth / 2,
      0,
      0, // 1: Bottom Right
      0,
      bladeHeight,
      0, // 2: Top Center
    ];

    const indices = [0, 1, 2];

    // UVs: important for the gradient (y) and wind flexibility
    const uvs = [
      0,
      0, // 0
      1,
      0, // 1
      0.5,
      1, // 2
    ];

    const normals = [0, 0, 1, 0, 0, 1, 0, 0, 1];

    const customMesh = new Mesh('grass_blade', this.scene);
    const vertexData = new VertexData();

    vertexData.positions = positions;
    vertexData.indices = indices;
    vertexData.uvs = uvs;
    vertexData.normals = normals;

    vertexData.applyToMesh(customMesh);

    this.grassMesh = customMesh;
    this.grassMesh.alwaysSelectAsActiveMesh = true; // Optimization for thin instances usually

    this.grassMesh.receiveShadows = config.receiveShadows;
  }

  private createGrassMaterial(config: GrassBuilderConfig): void {
    const vertexSource = `
    // Attributes
    attribute vec3 position;
    attribute vec3 normal;
    attribute vec2 uv;
    attribute vec4 world0;
    attribute vec4 world1;
    attribute vec4 world2;
    attribute vec4 world3;

    // Uniforms
    uniform mat4 viewProjection;
    uniform float iTime;
    uniform float windSpeed;
    uniform float windStrength;
    uniform vec3 frustumStart;
    uniform float frustumDepth;
    uniform vec3 frustumDirection;

    // Impacts
    uniform int impactCount;
    uniform float impactPosX[10];
    uniform float impactPosZ[10];
    uniform float impactRadius[10];
    uniform float impactStrength[10];

    // Varying
    varying vec3 vPosition;
    varying vec3 vNormal;
    varying vec2 vUV;
    varying float vHeight;

    // Simple noise function for wind variation
    float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
    }

    void main() {
        // Construct finalWorld matrix from the 4 attributes
        mat4 finalWorld = mat4(world0, world1, world2, world3);

        // Get world position
        vec4 worldPos = finalWorld * vec4(position, 1.0);
        vec4 baseWorldPos = finalWorld * vec4(0.0, 0.0, 0.0, 1.0); // Base position of this instance

        // Wind animation parameters
        float time = iTime * windSpeed;

        // Calculate wind influence based on height (base of blade doesn't move)
        // uv.y is 0 at bottom, 1 at top
        float flexibility = uv.y * uv.y;

        // Create organic wind movement using multiple sine waves
        // Use world position to offset phase so blades don't move in unison
        float windX = sin(time + worldPos.x * 0.5 + worldPos.z * 0.3);
        float windZ = cos(time * 0.8 + worldPos.x * 0.2 + worldPos.z * 0.5);
        float noise = sin(time * 2.0 + worldPos.x + worldPos.z); // Fast flutter

        // Combine waves
        vec3 windOffset = vec3(
            (windX + noise * 0.2) * windStrength * flexibility,
            0.0, // Minimal vertical movement
            (windZ + noise * 0.2) * windStrength * flexibility
        );

        // Apply wind to world position
        // Calculate impact offsets
        vec3 impactOffset = vec3(0.0);
        for (int i = 0; i < 10; i++) {
            if (i >= impactCount) break;

            float distSq = (baseWorldPos.x - impactPosX[i]) * (baseWorldPos.x - impactPosX[i]) +
                           (baseWorldPos.z - impactPosZ[i]) * (baseWorldPos.z - impactPosZ[i]);
            float rad = impactRadius[i];
            float radiusSq = rad * rad;

            if (distSq < radiusSq) {
                float dist = sqrt(distSq);
                float strength = 1.0 - (dist / rad); // 1.0 at center, 0.0 at edge

                // Random vector (-1 or 1) generated from world pos, to bend chaotically
                float randX = random(baseWorldPos.xz);
                float randZ = random(baseWorldPos.zx + vec2(1.0, 1.0));

                float dirX = randX > 0.5 ? 1.0 : -1.0;
                float dirZ = randZ > 0.5 ? 1.0 : -1.0;

                // High local wind factor, quickly bending
                float localWind = impactStrength[i]; // multiplier to simulate strong push
                float flutter = sin(iTime * 15.0 + baseWorldPos.x * 2.0) * 0.3; // rapid shaking

                impactOffset.x += dirX * (localWind + flutter) * strength * flexibility * windStrength;
                impactOffset.z += dirZ * (localWind + flutter) * strength * flexibility * windStrength;
            }
        }

        worldPos.xyz += windOffset + impactOffset;

        // Frustum Culling
        if (frustumDepth > 0.0) {
            vec3 toBlade = baseWorldPos.xyz - frustumStart;
            toBlade.y = 0.0;

            // Optimization: Use squared distance for the initial check to avoid a square root
            float distSq = dot(toBlade, toBlade);
            float frustumDepthSq = frustumDepth * frustumDepth;

            // Only calculate actual length if we are within the general culling radius
            if (distSq < frustumDepthSq) {
                float dist = sqrt(distSq);

                // Fade out over the last 20% of the radius (max 5 units)
                float fadeRadius = min(5.0, frustumDepth * 0.2);
                float scaleFade = clamp((frustumDepth - dist) / fadeRadius, 0.0, 1.0);

                // Don't cull if very close (inside a threshold) to prevent popping when looking down
                // (25.0 is 5.0 squared)
                if (distSq > 25.0) {
                    // Optimization: We don't need to normalize toBlade strictly if we just want a
                    // proportional dot product, but we do need the angle.
                    // Instead of two normalize() calls (which use invSqrt), we divide the dot product
                    // by the pre-calculated distance.
                    // Assumes frustumDirection is already normalized on the CPU (it is in Babylon).
                    vec2 dirXZ = toBlade.xz / dist;
                    vec2 forwardXZ = frustumDirection.xz; // We assume this is mostly normalized XZ

                    // Normalize forwardXZ roughly (since Y might not be 0, XZ length < 1)
                    // It's cheaper to do this on the CPU, but doing it here is still faster
                    // than the full vec3 normalize() we had before.
                    float fLenSq = dot(forwardXZ, forwardXZ);
                    if (fLenSq > 0.001) {
                        forwardXZ *= inversesqrt(fLenSq);
                    }

                    float dotProduct = dot(dirXZ, forwardXZ);

                    // dotProduct > 0 means in front. Allow slightly behind (-0.2) and soft fade
                    float angleFade = smoothstep(-0.2, 0.1, dotProduct);
                    scaleFade *= angleFade;
                }

                // Shrink the blade to the baseline when out of bounds
                worldPos.xyz = mix(baseWorldPos.xyz, worldPos.xyz, scaleFade);
            } else {
                // Completely out of bounds radius
                worldPos.xyz = baseWorldPos.xyz;
            }
        }

        // Set varyings
        vPosition = worldPos.xyz;
        vNormal = normalize(mat3(finalWorld) * normal); // Rotate normal by instance matrix
        vUV = uv;
        vHeight = uv.y;

        gl_Position = viewProjection * worldPos;
    }
    `;

    const fragmentSource = `
    precision highp float;

    // Uniforms
    uniform vec3 baseColor;
    uniform vec3 tipColor;
    uniform vec3 lightDirection; // Optional global light direction

    // Varying from Vertex Shader
    varying vec3 vPosition;
    varying vec3 vNormal;
    varying vec2 vUV;
    varying float vHeight;

    void main() {
        // Simple gradient mixture
        vec3 color = mix(baseColor, tipColor, vHeight);

        // Add slight random variation based on position to break uniformity
        // We use world position (vPosition) to create a pseudo-random factor
        float variation = sin(vPosition.x * 0.1 + vPosition.z * 0.1) * 0.05;
        color += variation;

        // Fake ambient occlusion at the bottom
        float ao = smoothstep(0.0, 0.4, vHeight);
        color *= (0.6 + 0.4 * ao);

        // Simple directional lighting (optional, keeps it cartoonish)
        // Assuming light comes generally from above/side
        vec3 lightDir = normalize(vec3(0.5, 1.0, 0.3));
        float diff = max(dot(vNormal, lightDir), 0.0);
        // Mix ambient with diffuse for softer look
        vec3 finalColor = color * (0.7 + 0.3 * diff);

        gl_FragColor = vec4(finalColor, 1.0);
    }
    `;

    this.shaderMaterial = new ShaderMaterial(
      'grass_material',
      this.scene,
      {
        vertexSource: vertexSource,
        fragmentSource: fragmentSource,
      },
      {
        attributes: ['position', 'normal', 'uv', 'world0', 'world1', 'world2', 'world3'],
        uniforms: [
          'worldViewProjection',
          'viewProjection',
          'view',
          'iTime',
          'windSpeed',
          'windStrength',
          'baseColor',
          'tipColor',
          'frustumStart',
          'frustumDepth',
          'frustumDirection',
          'impactCount',
          'impactPosX',
          'impactPosZ',
          'impactRadius',
        ],
        needAlphaBlending: false,
      }
    );

    this.shaderMaterial.backFaceCulling = false; // Double sided

    this.shaderMaterial.setFloat('windSpeed', config.windSpeed);
    this.shaderMaterial.setFloat('windStrength', config.windStrength);
    this.shaderMaterial.setColor3('baseColor', config.baseColor);
    this.shaderMaterial.setColor3('tipColor', config.tipColor);

    this.grassMesh.material = this.shaderMaterial;
  }

  private generateInstances(config: GrassBuilderConfig): void {
    if (!this.grassMesh) return;

    let instanceCount = 0;
    const tempMatrix = Matrix.Identity();
    const tempTranslation = Vector3.Zero();
    const tempRotation = Quaternion.Identity();
    const tempScaling = Vector3.One();

    const transformedMatrices = [];

    const count = config.positions.length;
    for (let i = 0; i < count; i++) {
      const position = config.positions[i];

      // Random rotation around Y
      const rotationY = this.floatInRange(0, Math.PI * 2);
      Quaternion.RotationAxisToRef(Vector3.Up(), rotationY, tempRotation);

      // Random scale
      const scale = this.floatInRange(config.minScale, config.maxScale);
      tempScaling.setAll(scale);

      tempTranslation.copyFrom(position);

      Matrix.ComposeToRef(tempScaling, tempRotation, tempTranslation, tempMatrix);

      const matrixCopy = new Float32Array(16);
      tempMatrix.copyToArray(matrixCopy);
      transformedMatrices.push(matrixCopy);
      instanceCount++;
    }

    if (instanceCount > 0) {
      // Create the thin instances
      // We need to flatten the array of Float32Arrays into one big Float32Array
      const buffer = new Float32Array(instanceCount * 16);
      for (let i = 0; i < instanceCount; i++) {
        buffer.set(transformedMatrices[i], i * 16);
      }

      this.grassMesh.thinInstanceSetBuffer('matrix', buffer, 16, true);
    }
  }

  /**
   * Helper to generate random positions on a ground mesh.
   */
  randomPositionsOnGround(ground: GroundMesh, count: number): Vector3[] {
    const positions = [];
    const minX = ground.position.x - (ground.scaling.x * ground._width) / 2;
    const maxX = ground.position.x + (ground.scaling.x * ground._width) / 2;
    const minZ = ground.position.z - (ground.scaling.z * ground._height) / 2;
    const maxZ = ground.position.z + (ground.scaling.z * ground._height) / 2;

    for (let i = 0; i < count; i++) {
      const x = this.rng() * (maxX - minX) + minX;
      const z = this.rng() * (maxZ - minZ) + minZ;
      const y = ground.getHeightAtCoordinates(x, z);
      positions.push(new Vector3(x, y, z));
    }
    return positions;
  }

  /**
   * Helper to get a random float within a range.
   */
  floatInRange(min: number, max: number): number {
    return this.rng() * (max - min) + min;
  }
}
